import React, { useState } from 'react';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { useToggle } from 'usehooks-ts';
import { useRouter } from 'next/router';

import {
  Button,
  InputAutoCompleteForCheckout,
  SectionTitle,
  Select,
  TextBlock,
} from '@/components/ui';
import { hooks as fiberHooks } from '@/domains/fiber';

import { CloseMobile } from '@/components/SvgComponents';
import RequestNewAddress from '../RequestNewAddressModal/RequestNewAddress';

import styles from './CheckConnection.module.scss';

const CheckConnection = ({ data }) => {
  const { t, lang } = useTranslation();
  const router = useRouter();
  const param = router.query;
  const [city, setCity] = useState();
  const [street, setStreet] = useState();
  const [house, setHouse] = useState();
  const [notFoundDialog, setNotFoundDialog] = useToggle(false);
  const [showCheckAddress, setShowCheckAddress] = useState(false);

  const { data: fiberCities } = fiberHooks.useFiberCities({ lang });
  const findDefaultCity = fiberCities?.find((el) => el?.key === '1');

  const { data: fiberStreets } = fiberHooks.useFiberStreets(
    {
      cityId: city?.key || findDefaultCity?.key,
      lang,
    },
    {
      skip: !(city || findDefaultCity),
    },
  );
  const filteredStreet = fiberStreets?.find(
    (el) => el?.name === (street || el?.key === param.street),
  );
  const { data: fiberHouses } = fiberHooks.useFiberHouses(
    {
      streetId: filteredStreet?.key,
      lang,
    },
    {
      skip: !filteredStreet,
    },
  );
  const filteredHouse = fiberHouses?.find(
    (el) => el?.name === house || el?.key === param.house,
  );

  const cityHandle = (value) => {
    setCity(value);
    setStreet('');
    setHouse('');
  };

  const streetHandle = (value) => {
    setStreet(value);
    setHouse('');
  };

  const handleCheckout = () => {
    if (!filteredHouse) {
      setNotFoundDialog();
    } else {
      router.push({
        pathname: '/fiber-check',
        query: {
          cityKey: city?.key || findDefaultCity?.key,
          cityName: city?.name || findDefaultCity?.name,
          streetKey: filteredStreet?.key,
          houseKey: filteredHouse?.key,
          houseName: filteredHouse?.name,
          streetName: filteredStreet?.name,
        },
      });
    }
  };

  const closeHandle = () => {
    setCity(findDefaultCity);
    setStreet('');
    setHouse('');
    setShowCheckAddress(!showCheckAddress);
  };
  return (
    <section
      className={classNames([
        'wrapper',
        styles.check__connection,
        { [styles.activeMobile]: showCheckAddress },
      ])}
      itemScope
      itemType='https://schema.org/Action'
    >
      <div className={styles.top}>
        <CloseMobile className={styles.close_btn} onClick={closeHandle} />
        {data?.title && (
          <SectionTitle titleModify='title--large'>{data?.title}</SectionTitle>
        )}
        {data?.description && (
          <TextBlock className={classNames('text--small')} isHtml>
            {data.description}
          </TextBlock>
        )}
      </div>
      <form className={styles.check__form}>
        <Select
          value={city || findDefaultCity}
          data={fiberCities}
          label={t('common:city')}
          onChange={cityHandle}
          className={styles.select}
          withoutIcon
          data-testid={'checkConnection-city'}
        />
        <InputAutoCompleteForCheckout
          label={t('common:street')}
          setValue={streetHandle}
          value={street}
          models={fiberStreets}
          classname={styles.fiber__input}
          id='street'
          required
        />
        <InputAutoCompleteForCheckout
          label={t('common:home')}
          setValue={setHouse}
          value={house}
          models={fiberHouses}
          classname={styles.fiber__input}
          id='house'
          disabled={!street}
          dataTestId={'checkConnection-house'}
        />
        <Button
          type='button'
          size='normal'
          className={styles.button}
          disabled={!house}
          onClick={handleCheckout}
          data-testid={'checkConnection-button'}
        >
          {data?.button_text}
        </Button>
      </form>
      <RequestNewAddress
        closeModalAction={setNotFoundDialog}
        active={notFoundDialog}
        city={city || findDefaultCity}
        street={street}
        house={house}
      />
      <div className={styles.mobile__btns}>
        <div className={styles.form__btns}>
          <Button
            variant='transparent'
            className={styles.cancel}
            onClick={closeHandle}
          >
            {t('fiber:form.card.cancel')}
          </Button>
          <Button
            onClick={handleCheckout}
            disabled={!house}
            className={styles.check}
          >
            {t('fiber:form.card.check')}
          </Button>
        </div>
        <Button
          className={styles.form_btn}
          onClick={() => setShowCheckAddress(true)}
        >
          {t('fiber:form.card.checkAddress')}
        </Button>
      </div>
    </section>
  );
};

export default CheckConnection;
